import { isNil } from 'lodash';
import { createContext, useCallback, useContext } from 'react';
import { useCardNavigationActions } from '@shared/scenes/cards/navigation';
import { useNavigator } from '@shared/util/navigation';
import { AskAnswerRef, AskCardsContext } from './AskCardsContext';
import { AskCardLink } from './extractCardLinks';

/**
 * The answer being rendered, for the cards in it to say which answer they were opened from. Set by
 * `AskAnswerMarkdown` around each answer's markdown.
 */
export const AskAnswerContext = createContext<AskAnswerRef | undefined>(undefined);

/**
 * Opens a card an answer names.
 *
 * On the Ask page the card opens over the page, with the answer's cards in the pane beside it (see
 * `AskCardsContext`). Anywhere else — the floating panel — it opens in its workspace, stepping
 * through `cards`, which is where following the card's link would have gone anyway.
 */
export function useOpenAskCard(): (card: AskCardLink, cards: AskCardLink[]) => void {
  const cardsContext = useContext(AskCardsContext);
  const answer = useContext(AskAnswerContext);
  const { navigate } = useNavigator();
  const { addStackItem } = useCardNavigationActions();

  return useCallback(
    (card: AskCardLink, cards: AskCardLink[]) => {
      if (!isNil(cardsContext) && !isNil(answer)) {
        cardsContext.openCard(card, answer);
        return;
      }
      addStackItem({
        entityId: card.cardId,
        channelSlug: card.channelSlug,
        entityIds: sameWorkspaceIds(card, cards),
      });
      navigate('CardUpdate', { cardId: card.cardId, channelSlug: card.channelSlug });
    },
    [cardsContext, answer, addStackItem, navigate],
  );
}

/** The ids next and previous step through: the answer's cards in the opened card's workspace. */
function sameWorkspaceIds(card: AskCardLink, cards: AskCardLink[]): string[] {
  return cards.filter(other => other.channelSlug === card.channelSlug).map(other => other.cardId);
}
